// client/src/dnd/emitter_tool/runtime/Runtime_Parts/CollisionManager.js
import * as PIXI from "pixi.js";

const EPS = 1e-6;

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
function dot(ax, ay, bx, by) { return ax * bx + ay * by; }
function norm(x, y) { const L = Math.hypot(x, y) || 1; return { x: x / L, y: y / L }; }
function reflect(vx, vy, nx, ny, r = 1) { const d = dot(vx, vy, nx, ny); return { x: (vx - 2 * d * nx) * r, y: (vy - 2 * d * ny) * r }; }
function projectTangential(vx, vy, nx, ny) { const d = dot(vx, vy, nx, ny); return { x: vx - d * nx, y: vy - d * ny }; }
function segDir(s) { const dx = s.x2 - s.x1, dy = s.y2 - s.y1, L = Math.hypot(dx, dy) || 1; return { tx: dx / L, ty: dy / L, nx: -(dy / L), ny: dx / L }; }

function rayCircleTOI(p0x, p0y, vx, vy, cx, cy, r) {
  const dx = p0x - cx, dy = p0y - cy;
  const a = vx*vx + vy*vy + EPS;
  const b = 2 * (vx*dx + vy*dy);
  const c = dx*dx + dy*dy - r*r;
  const disc = b*b - 4*a*c;
  if (disc < 0) return null;
  const s = Math.sqrt(Math.max(0, disc));
  const t1 = (-b - s) / (2*a);
  const t2 = (-b + s) / (2*a);
  let t = Number.POSITIVE_INFINITY;
  if (t1 >= 0 && t1 <= 1) t = Math.min(t, t1);
  if (t2 >= 0 && t2 <= 1) t = Math.min(t, t2);
  if (!Number.isFinite(t)) return null;
  const px = p0x + vx*t, py = p0y + vy*t;
  const nx = (px - cx), ny = (py - cy);
  const L = Math.hypot(nx, ny) || 1;
  return { t, nx: nx/L, ny: ny/L, cx, cy };
}

function sweepCircleVsSegmentCapsule(p0x, p0y, p1x, p1y, r, x1, y1, x2, y2) {
  const vx = p1x - p0x, vy = p1y - p0y;
  const sx = x2 - x1, sy = y2 - y1;
  const segLen = Math.hypot(sx, sy);
  if (segLen < 1e-7) return rayCircleTOI(p0x, p0y, vx, vy, x1, y1, r);
  const tx = sx / segLen, ty = sy / segLen;
  const nx = -ty, ny = tx;

  const hits = [];

  const denom = vx*nx + vy*ny;
  if (Math.abs(denom) > 1e-8) {
    const s0 = ((p0x - x1)*nx + (p0y - y1)*ny);
    const cand = [ ( r - s0) / denom, (-r - s0) / denom ];
    for (const t of cand) {
      if (t < 0 || t > 1) continue;
      const qx = p0x + vx*t, qy = p0y + vy*t;
      const u = ((qx - x1)*tx + (qy - y1)*ty) / segLen;
      if (u >= -1e-6 && u <= 1+1e-6) {
        const side = ((qx - x1)*nx + (qy - y1)*ny) >= 0 ? 1 : -1;
        hits.push({
          t,
          nx: nx*side,
          ny: ny*side,
          cx: x1 + tx * clamp(u, 0, 1) * segLen,
          cy: y1 + ty * clamp(u, 0, 1) * segLen
        });
      }
    }
  }

  const cap1 = rayCircleTOI(p0x, p0y, vx, vy, x1, y1, r);
  const cap2 = rayCircleTOI(p0x, p0y, vx, vy, x2, y2, r);
  if (cap1) hits.push(cap1);
  if (cap2) hits.push(cap2);

  if (!hits.length) return null;

  let best = hits[0];
  for (let i = 1; i < hits.length; i++) if (hits[i].t < best.t) best = hits[i];
  return { hit: true, time: best.t, nx: best.nx, ny: best.ny, cx: best.cx, cy: best.cy };
}

function asPt(p) {
  if (!p) return null;
  if (Array.isArray(p)) { const [x, y] = p; return (Number.isFinite(x) && Number.isFinite(y)) ? { x, y } : null; }
  if (Number.isFinite(p.x) && Number.isFinite(p.y)) return { x: p.x, y: p.y };
  return null;
}
function addSeg(out, x1, y1, x2, y2) { if ([x1, y1, x2, y2].every(Number.isFinite)) out.push({ x1, y1, x2, y2 }); }

function toSegments(raw) {
  const out = [];
  if (!raw) return out;
  let dpr = 1, space = null;
  if (!Array.isArray(raw) && typeof raw === "object") {
    if (Number.isFinite(raw.dpr) && raw.dpr > 0) dpr = raw.dpr;
    if (typeof raw.space === "string") space = raw.space;
  }
  const scale = space === "css" ? dpr : 1;

  const parseOne = (payload) => {
    if (!payload) return;
    const pick = payload.segments || payload.lines || payload.edges || null;
    if (Array.isArray(pick)) {
      for (const s of pick) {
        if (Array.isArray(s) && s.length >= 4 && s.every(Number.isFinite)) { const [x1,y1,x2,y2]=s; addSeg(out,x1*scale,y1*scale,x2*scale,y2*scale); }
        else if (s && typeof s === "object") {
          if ([s.x1,s.y1,s.x2,s.y2].every(Number.isFinite)) addSeg(out,s.x1*scale,s.y1*scale,s.x2*scale,s.y2*scale);
          else if (Array.isArray(s.c) && s.c.length>=4) { const [x1,y1,x2,y2]=s.c; addSeg(out,x1*scale,y1*scale,x2*scale,y2*scale); }
          else if (s.a||s.b||s.p1||s.p2) { const a=asPt(s.a||s.p1), b=asPt(s.b||s.p2); if(a&&b) addSeg(out,a.x*scale,a.y*scale,b.x*scale,b.y*scale); }
        }
      }
      return;
    }
    const polys = payload.polygons || payload.paths || null;
    const arr = Array.isArray(polys) ? polys : (Array.isArray(payload) ? payload : null);
    if (Array.isArray(arr) && arr.length && Array.isArray(arr[0])) {
      for (const poly of arr) {
        const pts = poly.map(asPt).filter(Boolean);
        for (let i=0;i<pts.length;i++){ const a=pts[i], b=pts[(i+1)%pts.length]; if(a&&b) addSeg(out,a.x*scale,a.y*scale,b.x*scale,b.y*scale); }
      }
      return;
    }
    if (Array.isArray(payload) && payload.length && payload[0] && payload[0].c) {
      for (const w of payload) if (Array.isArray(w.c) && w.c.length>=4) { const [x1,y1,x2,y2]=w.c; addSeg(out,x1*scale,y1*scale,x2*scale,y2*scale); }
      return;
    }
    if (Array.isArray(payload.paths)) {
      for (const path of payload.paths) {
        const pts=(path.points||path)??[], normPts=(Array.isArray(pts)?pts:[]).map(asPt).filter(Boolean);
        for (let i=0;i<normPts.length-1;i++){ const a=normPts[i], b=normPts[i+1]; if(a&&b) addSeg(out,a.x*scale,a.y*scale,b.x*scale,b.y*scale); }
      }
      return;
    }
    const r = payload.rect || payload.bounds || payload;
    if (r && Number.isFinite(r.width) && Number.isFinite(r.height)) {
      const x=Number(r.x)||0, y=Number(r.y)||0, w=r.width, h=r.height;
      addSeg(out,x*scale,y*scale,(x+w)*scale,y*scale);
      addSeg(out,(x+w)*scale,y*scale,(x+w)*scale,(y+h)*scale);
      addSeg(out,(x+w)*scale,(y+h)*scale,x*scale,(y+h)*scale);
      addSeg(out,x*scale,(y+h)*scale,x*scale,y*scale);
      return;
    }
    if (Array.isArray(payload) && payload.length>=4 && payload.every(Number.isFinite)) {
      const [x1,y1,x2,y2]=payload; addSeg(out,x1*scale,y1*scale,x2*scale,y2*scale);
    }
  };

  parseOne(raw);
  if (!out.length && typeof raw === "object" && !Array.isArray(raw)) {
    for (const k of ["segments","lines","edges","polygons","paths","data","walls"]) if (raw[k]) parseOne(raw[k]);
  }
  if (!out.length && Array.isArray(raw)) for (const it of raw) parseOne(it);
  return out;
}

function pickCollisionKeys(src) {
  const dst = {};
  for (const k of ["useWalls","wallResponse","useTokenCollision","tokenResponse","restitution","debugCollisions","clingFactor"]) {
    if (k in (src || {})) dst[k] = src[k];
  }
  return dst;
}
function digest(obj) { try { return JSON.stringify(obj); } catch { return ""; } }

export default class CollisionManager {
  constructor({ getWalls, walls, wallsSpace, wallsDpr, getTokens, getToken, settings = {}, dispatch = () => {}, space = "image", boundsRect = null } = {}) {
    this.getWalls = typeof getWalls === "function" ? getWalls : null;
    this._rawWalls = walls || null;
    this._rawWallsSpace = wallsSpace || null;
    this._rawWallsDpr = Number.isFinite(wallsDpr) ? wallsDpr : null;

    this.getTokens = typeof getTokens === "function" ? getTokens : null;
    this.getToken = typeof getToken === "function" ? getToken : null;
    this.dispatch = typeof dispatch === "function" ? dispatch : () => {};
    this.space = space;

    this.settings = { useWalls: true, wallResponse: "bounce", restitution: 1, debugCollisions: false, clingFactor: 0.15, ...settings };
    this._settingsDigest = digest(pickCollisionKeys(this.settings));

    this._segments = [];
    this._builtFromHash = "";
    this._boundsRect = boundsRect && Number.isFinite(boundsRect.width) && Number.isFinite(boundsRect.height)
      ? { x: +boundsRect.x || 0, y: +boundsRect.y || 0, width: +boundsRect.width, height: +boundsRect.height }
      : null;

    this._debugEnabled = false;
    this._debugParent = null;
    this._dbgAll = null;
    this._dbgHit = null;
    this._dbgTokens = null;

    if (typeof window !== "undefined") {
      window.addEventListener("EmitterTool:WallsUpdated", (e) => {
        const d = e?.detail || {};
        if (d.segments && Array.isArray(d.segments)) {
          this.setWalls({ segments: d.segments, space: "image" });
        } else if (d.polys && Array.isArray(d.polys)) {
          this.setWalls({ polygons: d.polys, space: "image" });
        }
        if (this._debugEnabled) this._drawAllSegments();
      });

      window.addEventListener("EmitterTool:CollisionSettings", (e) => {
        const d = pickCollisionKeys(e?.detail || {});
        if (Object.keys(d).length) this.updateSettings(d);
      });

      if (window.__EmitterWallsLast) {
        this.setWalls(window.__EmitterWallsLast);
      }
    }

    this._rebuild(true);
  }

  attachDebugTo(container) {
    if (!container || !(container instanceof PIXI.Container)) {
      return;
    }
    this._debugParent = container;
    try { this._debugParent.sortableChildren = true; } catch {}
    this._ensureDebugLayers();
    this._drawAllSegments();
    if (!this._segments.length) {
    }
  }
  setShowDebugWalls(flag = true) { const want = !!flag; if (want === this._debugEnabled) return; this._debugEnabled = want; if (want) { this._ensureDebugLayers(); this._drawAllSegments(); } else { this._destroyDebugLayers(); } }
  setDebugCollisions(v = true) { this.updateSettings({ debugCollisions: !!v }); }
  enableDebug(container) { this.attachDebugTo(container); this.setShowDebugWalls(true); }
  debugRedraw() { this._drawAllSegments(); }

  _ensureDebugLayers() {
    if (!this._debugParent || !this._debugEnabled) return;
    const mk = (name, z) => {
      const g = new PIXI.Graphics();
      g.name = name;
      g.zIndex = z;
      g.alpha = 1;
      g.visible = true;
      g.renderable = true;
      g.blendMode = PIXI.BLEND_MODES.NORMAL;
      g.eventMode = "none";
      this._debugParent.addChild(g);
      return g;
    };
    if (!this._dbgAll || this._dbgAll.destroyed || !this._dbgAll.parent) this._dbgAll = mk("CM:AllWalls", 999995);
    if (!this._dbgHit || this._dbgHit.destroyed || !this._dbgHit.parent) this._dbgHit = mk("CM:HitWall", 999996);
    if (!this._dbgTokens || this._dbgTokens.destroyed || !this._dbgTokens.parent) this._dbgTokens = mk("CM:TokenHits", 999997);
  }
  _destroyDebugLayers() {
    const kill = (g) => { if (!g) return; try { if (g.parent) g.parent.removeChild(g); g.destroy({ children: false }); } catch {} };
    kill(this._dbgAll);
    kill(this._dbgHit);
    kill(this._dbgTokens);
    this._dbgAll = null;
    this._dbgHit = null;
    this._dbgTokens = null;
  }
  _drawAllSegments() {
    if (!this._debugEnabled || !this._dbgAll) return;
    const g = this._dbgAll;
    g.clear();
    const n = this._segments.length;
    if (!n) return;
    g.lineStyle(3, 0xff3333, 1);
    for (const s of this._segments) { g.moveTo(s.x1, s.y1); g.lineTo(s.x2, s.y2); }
    g.lineStyle(0);
    g.beginFill(0xff3333, 1);
    for (const s of this._segments) { g.drawCircle(s.x1, s.y1, 2.5); g.drawCircle(s.x2, s.y2, 2.5); }
    g.endFill();
    g.alpha = 1; g.visible = true; g.renderable = true;
  }
  _drawHit(best, p, dt, preX, preY, endX, endY) {
    if (!this._debugEnabled || !this._dbgHit) return;
    const g = this._dbgHit;
    g.clear();
    g.lineStyle(4, 0xffe066, 1);
    g.moveTo(best.seg.x1, best.seg.y1); g.lineTo(best.seg.x2, best.seg.y2);
    g.lineStyle(3, 0x33e1ff, 0.9); g.moveTo(p.x, p.y); g.lineTo(preX, preY);
    g.lineStyle(3, 0x3380ff, 0.6); g.moveTo(preX, preY); g.lineTo(endX, endY);
    g.beginFill(0xffe066, 1); g.drawCircle(best.cx, best.cy, 3.5); g.endFill();
    g.lineStyle(3, 0xffe066, 1); g.moveTo(best.cx, best.cy); g.lineTo(best.cx + best.nx * 28, best.cy + best.ny * 28);
    g.alpha = 1; g.visible = true; g.renderable = true;
  }

  _logHit(best, p, dt) {
    if (!this.settings.debugCollisions) return;
  }

  raycastFat(x0, y0, x1, y1, fatPx = 0) {
    if (!this._segments?.length) return null;
    let best = null, bestT = 1;
    for (const s of this._segments) {
      const h = sweepCircleVsSegmentCapsule(x0, y0, x1, y1, Math.max(0, fatPx), s.x1, s.y1, s.x2, s.y2);
      if (h && h.time < bestT) { best = { ...h, seg: s }; bestT = h.time; }
    }
    return best;
  }
  lineOfSightVisibleFat(x0, y0, x1, y1, fatPx = 0) { return !this.raycastFat(x0, y0, x1, y1, fatPx); }

  setWalls(raw, { space = this._rawWallsSpace, dpr = this._rawWallsDpr } = {}) {
    this._rawWalls = raw || null;
    this._rawWallsSpace = space || null;
    this._rawWallsDpr = Number.isFinite(dpr) ? dpr : null;

    const segsFlat = toSegments(raw);

    this._rebuild(true);

    if (this._debugEnabled) {
      this._ensureDebugLayers();
      this._drawAllSegments();
      if (this._dbgHit) this._dbgHit.clear();
      if (this._dbgTokens) this._dbgTokens.clear();
    }
  }
  getSegments() { return Array.isArray(this._segments) ? this._segments.slice() : []; }
  hasWalls() { return Array.isArray(this._segments) && this._segments.length > 0; }

  updateSettings(next) {
    if (!next || typeof next !== "object") return;
    const subset = pickCollisionKeys(next);
    if (!Object.keys(subset).length) return;

    const prevDigest = this._settingsDigest;
    const merged = { ...this.settings, ...subset };
    const nextDigest = digest(pickCollisionKeys(merged));
    if (nextDigest === prevDigest) return;

    const prevWalls = !!this.settings.useWalls;
    const prevToken = !!this.settings.useTokenCollision;

    this.settings = merged;
    this._settingsDigest = nextDigest;

    const curWalls = !!this.settings.useWalls;
    const curToken = !!this.settings.useTokenCollision;

    const effectiveWalls = curWalls && this.hasWalls();
    const effectiveToken = curToken;
  }

  _hashWalls(raw) {
    let space=null, dpr=null;
    if (raw && typeof raw === "object" && !Array.isArray(raw)) { space = raw.space ?? this._rawWallsSpace ?? null; dpr = Number.isFinite(raw.dpr) ? raw.dpr : (this._rawWallsDpr ?? null); }
    else { space = this._rawWallsSpace ?? null; dpr = this._rawWallsDpr ?? null; }
    try { return JSON.stringify({ raw, space, dpr })?.slice(0,4096) || ""; } catch { return String(Date.now()); }
  }

  _pickWalls() {
    if (this._rawWalls) {
      if (this._rawWallsSpace || this._rawWallsDpr) {
        if (this._rawWalls && typeof this._rawWalls === "object" && !Array.isArray(this._rawWalls))
          return { ...this._rawWalls, space: this._rawWallsSpace ?? this._rawWalls.space, dpr: this._rawWallsDpr ?? this._rawWalls.dpr };
        return { data: this._rawWalls, space: this._rawWallsSpace, dpr: this._rawWallsDpr };
      }
      return this._rawWalls;
    }
    if (this.getWalls) { try { return this.getWalls() || null; } catch { return null; } }
    return null;
  }

  _rebuild(force = false) {
    const raw = this._pickWalls();
    const h = this._hashWalls(raw);
    if (!force && h === this._builtFromHash) return;

    let segs = toSegments(raw);

    if (!segs.length && this._boundsRect) {
      const { x, y, width, height } = this._boundsRect;
      segs = [
        { x1: x,         y1: y,          x2: x + width, y2: y          },
        { x1: x + width, y1: y,          x2: x + width, y2: y + height },
        { x1: x + width, y1: y + height, x2: x,         y2: y + height },
        { x1: x,         y1: y + height, x2: x,         y2: y          },
      ];
    }

    this._segments = segs;
    this._builtFromHash = h;

    if (this._debugEnabled) {
      this._ensureDebugLayers();
      this._drawAllSegments();
      if (this._dbgHit) this._dbgHit.clear();
      if (this._dbgTokens) this._dbgTokens.clear();
    }
  }

  _tokenCollide(p, dt) {
    let tokenSrc = null;
    if (this.getTokens) {
      try { tokenSrc = this.getTokens(); } catch { tokenSrc = null; }
    } else if (this.getToken) {
      try { tokenSrc = this.getToken(); } catch { tokenSrc = null; }
    }

    let tokens = [];
    if (Array.isArray(tokenSrc)) tokens = tokenSrc.filter(Boolean);
    else if (tokenSrc) tokens = [tokenSrc];

    if (typeof window !== "undefined") {
      const merged = [];
      const addSrc = (src) => {
        if (!src) return;
        if (Array.isArray(src)) {
          for (const t of src) if (t) merged.push(t);
        } else if (typeof src === "object") {
          for (const v of Object.values(src)) if (v) merged.push(v);
        }
      };
      addSrc(window.__PA_COLLISION_TOKENS_IMAGE);
      addSrc(window.__PA_COLLISION_TOKENS);
      addSrc(window.__PA_TOKENS);
      if (merged.length) tokens = tokens.concat(merged);
    }

    if (!tokens.length) return false;

    const p0x = p.x;
    const p0y = p.y;
    const vx = p.vx * dt;
    const vy = p.vy * dt;

    let bestTok = null;
    let bestT = 1;
    let bestR = 0;

    let globalMetreInPixels = 0;
    if (typeof window !== "undefined" && Number.isFinite(window.__PA_METRE_IN_PIXELS) && window.__PA_METRE_IN_PIXELS > 0) {
      globalMetreInPixels = window.__PA_METRE_IN_PIXELS;
    }

    const ignoreIdSet = new Set(
      (Array.isArray(this.settings.ignoreCollisionIds) ? this.settings.ignoreCollisionIds : [])
        .map((id) => (id == null ? null : String(id)))
        .filter(Boolean)
    );
    const sourceTokenId = this.settings.sourceTokenId != null ? String(this.settings.sourceTokenId) : null;
    const sourceTokenType = this.settings.sourceTokenType ? String(this.settings.sourceTokenType).toLowerCase() : null;

    for (const tok of tokens) {
      if (!tok) continue;

      const tokenIdCandidates = [
        tok.id,
        tok.source_id,
        tok.token_id,
        tok.sourceTokenId,
      ]
        .map((id) => (id == null ? null : String(id)))
        .filter(Boolean);

      const tokenType = tok.token_type != null
        ? String(tok.token_type).toLowerCase()
        : tok.type != null
        ? String(tok.type).toLowerCase()
        : null;

      const compositeIds = [];
      for (const id of tokenIdCandidates) {
        compositeIds.push(id);
        if (tokenType) {
          compositeIds.push(`character:${tokenType}:${id}`);
        }
      }

      if (sourceTokenId && tokenIdCandidates.includes(sourceTokenId)) continue;
      if (sourceTokenId && sourceTokenType && compositeIds.includes(`character:${sourceTokenType}:${sourceTokenId}`)) continue;
      if (compositeIds.some((id) => ignoreIdSet.has(id))) continue;

      const cx = Number(tok.x) || 0;
      const cy = Number(tok.y) || 0;

      let metreInPixels = globalMetreInPixels;
      if (Number.isFinite(tok.metre_in_pixels) && tok.metre_in_pixels > 0) {
        metreInPixels = tok.metre_in_pixels;
      }

      let rTokPx = 0;
      if (metreInPixels > 0) {
        rTokPx = metreInPixels * 0.5;
      } else {
        const rTokRaw = Number(tok.radius);
        if (Number.isFinite(rTokRaw) && rTokRaw > 0) rTokPx = rTokRaw;
      }

      const rr = (p.radius || 0) + (rTokPx > 0 ? rTokPx : 0);
      if (rr <= 0) continue;

      const dx0 = p0x - cx;
      const dy0 = p0y - cy;
      const dist0Sq = dx0 * dx0 + dy0 * dy0;
      let tHit = null;

      if (dist0Sq <= rr * rr + 1e-6) {
        tHit = 0;
      } else {
        const hit = rayCircleTOI(p0x, p0y, vx, vy, cx, cy, rr);
        if (!hit) continue;
        tHit = hit.t;
      }

      if (tHit < 0 || tHit > 1) continue;
      if (tHit < bestT) {
        bestT = tHit;
        bestTok = { cx, cy };
        bestR = rr;
      }
    }

    if (!bestTok) return false;

    const impactX = p0x + vx * bestT;
    const impactY = p0y + vy * bestT;

    const dx = impactX - bestTok.cx;
    const dy = impactY - bestTok.cy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    let nx = dx / dist;
    let ny = dy / dist;
    const rr = bestR || dist;

    const resp = String(this.settings.tokenResponse || "none").toLowerCase();

    if (this._debugEnabled && this._dbgTokens) {
      const g = this._dbgTokens;
      g.clear();
      g.lineStyle(3, 0xffff00, 1);
      g.drawCircle(bestTok.cx, bestTok.cy, rr);
      g.moveTo(bestTok.cx, bestTok.cy);
      g.lineTo(bestTok.cx + nx * rr, bestTok.cy + ny * rr);
      g.alpha = 1;
      g.visible = true;
      g.renderable = true;
    }

    if (this.settings.debugCollisions) {
      try {
        this.dispatch("EmitterTool:TokenCollisionHit", {
          x: impactX,
          y: impactY,
          tokenCx: bestTok.cx,
          tokenCy: bestTok.cy,
          radius: rr,
          response: resp,
          particle: {
            x: p.x,
            y: p.y,
            vx: p.vx,
            vy: p.vy,
            radius: p.radius,
          },
        });
      } catch {}
    }

    const placeOnSurface = () => {
      const R = rr || dist;
      p.x = bestTok.cx + nx * R;
      p.y = bestTok.cy + ny * R;
    };

    switch (resp) {
      case "kill":
        return true;

      case "stop": {
        placeOnSurface();
        p.vx = 0;
        p.vy = 0;
        return false;
      }

      case "bounce": {
        placeOnSurface();
        const r = Math.max(0, Number(this.settings.restitution ?? 1));
        const rv = reflect(p.vx, p.vy, nx, ny, r);
        p.vx = rv.x;
        p.vy = rv.y;
        return false;
      }

      case "travel": {
        placeOnSurface();
        const tV = projectTangential(p.vx, p.vy, nx, ny);
        p.vx = tV.x;
        p.vy = tV.y;
        return false;
      }

      case "cling": {
        placeOnSurface();
        const tV = projectTangential(p.vx, p.vy, nx, ny);
        p.vx = tV.x * 0.2;
        p.vy = tV.y * 0.2;
        return false;
      }

      case "hardblock": {
        placeOnSurface();
        p.vx = 0;
        p.vy = 0;
        return false;
      }

      case "flowaround": {
        placeOnSurface();
        const tV = projectTangential(p.vx, p.vy, nx, ny);
        p.vx = tV.x;
        p.vy = tV.y;
        return false;
      }

      case "softobstruction": {
        if (Math.random() < 0.3) {
          return false;
        }
        placeOnSurface();
        const tV = projectTangential(p.vx, p.vy, nx, ny);
        p.vx = tV.x * 0.6;
        p.vy = tV.y * 0.6;
        return false;
      }

      case "coreaura": {
        const coreR = rr * 0.7;
        if (dist <= coreR) {
          p.vx = 0;
          p.vy = 0;
          return false;
        }
        placeOnSurface();
        const tV = projectTangential(p.vx, p.vy, nx, ny);
        p.vx = tV.x * 0.7;
        p.vy = tV.y * 0.7;
        return false;
      }

      case "facingaware": {
        placeOnSurface();
        const tV = projectTangential(p.vx, p.vy, nx, ny);
        p.vx = tV.x;
        p.vy = tV.y;
        return false;
      }

      default:
        return false;
    }
  }

  _wallsCollide(p, dt) {
    const endX = p.x + p.vx * dt, endY = p.y + p.vy * dt;
    if (!this._segments.length) { p.x = endX; p.y = endY; return false; }

    let best = null, bestT = 1.0;
    for (const s of this._segments) {
      const hit = sweepCircleVsSegmentCapsule(p.x, p.y, endX, endY, p.radius || 0, s.x1, s.y1, s.x2, s.y2);
      if (!hit) continue;
      if (hit.time < bestT) { best = { ...hit, seg: s }; bestT = hit.time; }
    }

    if (!best) { p.x = endX; p.y = endY; return false; }

    const preX = p.x + (p.vx * dt) * best.time;
    const preY = p.y + (p.vy * dt) * best.time;

    this._drawHit(best, p, dt, preX, preY, endX, endY);
    this._logHit(best, p, dt);

    const sep = Math.max(0.01, (p.radius || 0) * 1.001);
    p.x = preX + best.nx * sep;
    p.y = preY + best.ny * sep;

    const response = (this.settings.wallResponse ?? (this.settings.useWalls ? "stop" : "none")).toString().toLowerCase();
    let { nx, ny } = best;
    { const nL = Math.hypot(nx, ny) || 1; nx /= nL; ny /= nL; }
    const rem = Math.max(0, 1 - best.time);

    switch (response) {
      case "kill":
        return true;

      case "stop": {
        p.vx = 0; p.vy = 0;
        return false;
      }

      case "bounce": {
        const speedIn = Math.hypot(p.vx, p.vy) || 0;
        let rv = reflect(p.vx, p.vy, nx, ny, 1);
        const rvLen = Math.hypot(rv.x, rv.y) || 1;
        rv.x *= (speedIn / rvLen);
        rv.y *= (speedIn / rvLen);
        const e = Number.isFinite(this.settings.restitution) ? Math.max(0, this.settings.restitution) : 1;
        rv.x *= e; rv.y *= e;
        p.vx = rv.x; p.vy = rv.y;
        if (rem > 0) { p.x += p.vx * dt * rem; p.y += p.vy * dt * rem; }
        return false;
      }

      case "travel": {
        const tv = projectTangential(p.vx, p.vy, nx, ny);
        p.vx = tv.x; p.vy = tv.y;
        if (rem > 0) { p.x += p.vx * dt * rem; p.y += p.vy * dt * rem; }
        return false;
      }

      case "cling": {
        const { tx, ty } = segDir(best.seg);
        const vLen = Math.hypot(p.vx, p.vy) || 0;
        const sign = dot(p.vx, p.vy, tx, ty) >= 0 ? 1 : -1;
        const cf = Math.max(0, Number(this.settings.clingFactor ?? 0.15));
        p.vx = tx * vLen * cf * sign;
        p.vy = ty * vLen * cf * sign;
        if (rem > 0) { p.x += p.vx * dt * rem; p.y += p.vy * dt * rem; }
        return false;
      }

      default: {
        if (rem > 0) { p.x += p.vx * dt * rem; p.y += p.vy * dt * rem; }
        return false;
      }
    }
  }

  handleParticle(p, dt) {
    const useWalls = this.hasWalls() && this.settings.useWalls !== false;
    const useToken = !!this.settings.useTokenCollision;

    if (useWalls) {
      const killed = this._wallsCollide(p, dt);
      if (killed) return true;
    } else {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }

    if (useToken) {
      const killed = this._tokenCollide(p, dt);
      if (killed) return true;
    }
    return false;
  }
}

try {
  if (typeof module !== "undefined" && module.exports) {
    module.exports = CollisionManager;
    module.exports.default = CollisionManager;
  }
} catch {}
